import { useRouter } from '@/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthData, useAuthActions } from '@/store/auth';
import { ApiErrorCode } from '@nf/utils-common/constants';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { deleteUserSession } from '@nf/services/actions/auth/session';
import { siteService } from '@/services/site.service';
import { LicenseeTypeEnums } from '@nf/types/site';
import { modalService } from '@/services/modal.service';
import { useEffect } from 'react';

export const useAccountError = () => {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { userName, siteName, uuid } = useAuthData() ?? {};
	const { signOut } = useAuthActions();
	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;

	const signOutAndRedirect = async (path: string) => {
		if (userName && siteName && uuid) {
			await deleteUserSession({
				name: userName,
				site: siteName,
				uuid
			});
			queryClient.clear();
			signOut();
		}

		router.push(path);
	};

	const alertAndSignOut = (errorCode?: string, errorMsg?: string) => {
		modalService.openJupiterLogoutMessageModal({
			title: errorCode || '',
			message: errorMsg || '',
			eventKey: 'JupiterLogout'
		});
	}

	useEffect(() => {
		const subscription = modalService.onOpenJupiterLogoutMessageModal
			.subscribe((state) => {
				if (state.props?.eventKey === 'JupiterLogout' && !state.isOpen) {
					router.replace('/auth/logout');
				}
			});

		return () => { 
			subscription.unsubscribe();
		}
	}, [router]);
	
	const handleAccountError = (errorCode?: string, errorMsg?: string, logId?: string) => {
		if (!errorCode) return;
		
		if (errorCode.startsWith('SP-1067')) {
			const webErrorCode =  errorCode.split('_')[0];
			const singleWalletErrorCode = errorCode.split('_')[1];
			signOutAndRedirect(`/app-error?errorcode=${webErrorCode}&singleWalletErrorCode=${singleWalletErrorCode}&logId=${logId}`);
			return;
		}

		matchEquals(errorCode)
			.on(ApiErrorCode.UM, () => {
				signOutAndRedirect('/um');
			})
			.on(ApiErrorCode.LoginChekckinUM, () => {
				signOutAndRedirect('/um');
			})
			.on(ApiErrorCode.GetBalanceError, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.LoginCheckinKickout, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.Multilogin, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.SysIdle, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.SysLogout, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.SysError, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}&logId=${logId}`);
			})
			.on(ApiErrorCode.InvalidToken, () => {
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${ApiErrorCode.InvalidToken}`);
			})
			.on(ApiErrorCode.IpBlock, () => {
				signOutAndRedirect('/limit');
			})
			.on(ApiErrorCode.LoginChekckinIpBlock302, () => {
				signOutAndRedirect('/limit');
			})
			.otherwise(() => {
				console.error(`${errorCode}: ${errorMsg}`);
				isAthena ? alertAndSignOut(errorCode, errorMsg) : signOutAndRedirect(`/app-error?errorcode=${errorCode}`);
			})
	};

	return { handleAccountError };
};